<template>
    <div class="form-dashboard">
        <div id="TrashBinList">
            <div class="header">
                <div class="header-logo">
                    <span class="md-icon">delete_outline</span>
                </div>
                <div class="header-text">
                    <h1>{{ $t('form.trashBinList.title') }}</h1>
                    <p>{{ $t('form.trashBinList.description') }}</p>
                </div>
            </div>
            <div class="container">
                <!-- Loading State -->
                <div v-if="loading" class="loading-container">
                    <div class="loading-spinner"></div>
                    <p class="loading-text">{{ $t('form.trashBinList.loading') }}</p>
                </div>

                <!-- Form List -->
                <div v-else-if="deletedFormsWithColors.length > 0" class="form-grid">
                    <div class="list-card" :class="{
                        selected: selectedForm && selectedForm._id === form._id
                    }" v-for="form in deletedFormsWithColors" :key="form._id" :style="{ '--border-color': form.color }"
                        @click="formId(form._id)">
                        <div class="card-header">
                            <div class="icon-section" :style="{ backgroundColor: form.color }">
                                <i class="md-icon">article</i>
                            </div>
                            <div class="text-section">
                                <div class="title">{{ form.title }}</div>
                                <div class="deleted-date">{{ formatDeletedDate(form.trashedAt) }}</div>
                                <div class="actor-info">
                                    <span v-if="form.createdBy && form.createdBy.name" class="actor-item">
                                        <i class="md-icon">person_add</i>{{ form.createdBy.name }}
                                    </span>
                                    <span v-if="form.trashedBy && form.trashedBy.name" class="actor-item actor-trashed">
                                        <i class="md-icon">delete</i>{{ form.trashedBy.name }}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div class="action-section">
                            <button class="action-btn restore" @click.stop="showRestoreModal(form)"
                                v-tippy="{ content: $t('form.trashBinList.restore'), placement: 'top' }">
                                <i class="md-icon">restore</i>
                                <span>{{ $t('form.trashBinList.restore') }}</span>
                            </button>
                            <button class="action-btn permanent-delete" @click.stop="showPermanentDeleteModal(form)"
                                v-tippy="{ content: $t('form.trashBinList.permanentDelete'), placement: 'top' }">
                                <i class="md-icon">delete_forever</i>
                                <span>{{ $t('form.trashBinList.delete') }}</span>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Empty State -->
                <div v-else class="empty-state">
                    <div class="empty-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M3 6h18"/>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                        </svg>
                    </div>
                    <h3>{{ $t('form.trashBinList.emptyTitle') }}</h3>
                    <p>{{ $t('form.trashBinList.emptyDescription') }}</p>
                </div>
            </div>
        </div>

        <div class="form-details" v-if="selectedForm">
            <div class="details-card">
                <h3 class="card-title">{{ selectedForm.title || '-' }}</h3>
                <span class="deleted-time">{{ formatDeletedDate(selectedForm.trashedAt) }}</span>

                <ul class="info-list">
                    <li v-if="selectedForm.createdBy && selectedForm.createdBy.name">
                        <span class="label">{{ $t('form.trashBinList.createdBy') }}</span>
                        <span class="value">{{ selectedForm.createdBy.name }}</span>
                    </li>
                    <li v-if="selectedForm.trashedBy && selectedForm.trashedBy.name">
                        <span class="label">{{ $t('form.trashBinList.trashedBy') }}</span>
                        <span class="value">{{ selectedForm.trashedBy.name }}</span>
                    </li>
                    <li>
                        <span class="label">{{ $t('form.trashBinList.totalResponses') }}</span>
                        <span class="value">{{ selectedForm.total || 0 }}</span>
                    </li>
                    <li>
                        <span class="label">{{ $t('form.trashBinList.unread') }}</span>
                        <span class="value">{{ selectedForm.unreadCount || 0 }}</span>
                    </li>
                    <li>
                        <span class="label">{{ $t('form.trashBinList.read') }}</span>
                        <span class="value">{{ selectedForm.readCount || 0 }}</span>
                    </li>
                    <li>
                        <span class="label">{{ $t('form.trashBinList.archived') }}</span>
                        <span class="value">{{ selectedForm.archivedCount || 0 }}</span>
                    </li>
                </ul>

                <div class="action-buttons">
                    <button class="btn-restore" @click="showRestoreModal(selectedForm)">
                        <div style="display: flex;justify-content: center;align-items: center;gap: 5px;">
                            <div><i class="md-icon">restore</i></div>
                            <div style="font-size: 12px; font-weight: bold;">{{ $t('form.trashBinList.restore') }}</div>
                        </div>
                    </button>
                    <button class="btn-delete" @click="showPermanentDeleteModal(selectedForm)">
                        <div style="display: flex;justify-content: center;align-items: center;gap: 5px;">
                            <div><i class="md-icon">delete_forever</i></div>
                            <div style="font-size: 12px; font-weight: bold;">{{ $t('form.trashBinList.delete') }}</div>
                        </div>
                    </button>
                </div>
            </div>
        </div>

        <!-- Restore Modal -->
        <DeleteModal v-if="restoreModal"
            :description="$t('form.trashBinList.restoreConfirmDescription')"
            :btnCancelText="$t('message.cancel')"
            :btnoktext="$t('form.trashBinList.restore')"
            @cancel="restoreModal = false"
            @confirm="confirmRestore" />

        <!-- Permanent Delete Modal -->
        <DeleteModal v-if="permanentDeleteModal"
            :description="$t('form.trashBinList.deleteConfirmDescription')"
            :btnCancelText="$t('message.cancel')"
            :btnoktext="$t('form.trashBinList.permanentDelete')"
            @cancel="permanentDeleteModal = false"
            @confirm="confirmPermanentDelete" />
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import DeleteModal from "../../Tasks/components/DeleteModal.vue";

export default {
    name: "TrashBinList",
    components: {
        DeleteModal,
    },
    data() {
        return {
            restoreModal: false,
            permanentDeleteModal: false,
            selectedForm: null,
            formToRestore: null,
            formToDelete: null,
            loading: false,
        };
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
            deletedForms: (state) => state.forms.deletedForms,
        }),
        deletedFormsWithColors() {
            return this.deletedForms.map((form) => {
                return {
                    ...form,
                    color: this.getRandomColor(form._id),
                };
            });
        },
    },
    methods: {
        ...mapActions({
            getDeletedForms: "forms/getDeletedForms",
            restoreForm: "forms/restoreForm",
            permanentDeleteForm: "forms/permanentDeleteForm",
        }),
        getRandomColor(seed) {
            const colors = [
                "#8B0000",
                "#A52A2A",
                "#800000",
                "#2F4F4F",
                "#556B2F",
                "#8B4513",
                "#483D8B",
                "#2E8B57",
                "#4682B4",
                "#6A5ACD",
                "#4B0082",
                "#708090",
                "#696969",
                "#808080",
                "#2F4F4F",
                "#3CB371",
                "#8B008B",
                "#9932CC",
            ];
            let hash = 0;
            for (let i = 0; i < seed.length; i++) {
                hash = seed.charCodeAt(i) + ((hash << 5) - hash);
            }
            return colors[Math.abs(hash) % colors.length];
        },
        formatDeletedDate(trashedAt) {
            if (!trashedAt) return "";
            const date = this.$moment(trashedAt);
            const now = this.$moment();
            const diffDays = now.diff(date, "days");

            if (diffDays === 0) {
                return this.$t("form.trashBinList.deletedToday");
            } else if (diffDays === 1) {
                return this.$t("form.trashBinList.deletedYesterday");
            } else if (diffDays < 7) {
                return this.$t("form.trashBinList.deletedDaysAgo", { count: diffDays });
            } else if (diffDays < 30) {
                const weeks = Math.floor(diffDays / 7);
                return this.$t("form.trashBinList.deletedWeeksAgo", { weeks: weeks });
            } else if (diffDays < 365) {
                const months = Math.floor(diffDays / 30);
                return this.$t("form.trashBinList.deletedMonthsAgo", { months: months });
            } else {
                return date.format("DD.MM.YYYY");
            }
        },
        async fetchDeletedForms() {
            this.loading = true;
            try {
                await this.getDeletedForms(this.site._id);
                if (this.deletedForms && this.deletedForms.length > 0) {
                    this.selectedForm = this.deletedForms[0];
                }
            } catch (error) {
                console.error("Silinmiş formlar yüklenemedi:", error);
            } finally {
                this.loading = false;
            }
        },
        formId(formId) {
            this.selectedForm = this.deletedForms.find((form) => form._id === formId);
        },
        showRestoreModal(form) {
            this.formToRestore = form;
            this.restoreModal = true;
        },
        showPermanentDeleteModal(form) {
            this.formToDelete = form;
            this.permanentDeleteModal = true;
        },
        async confirmRestore() {
            if (this.formToRestore) {
                try {
                    await this.restoreForm({ siteId: this.site._id, formId: this.formToRestore._id });
                    this.$message.success(this.$t("form.trashBinList.restoreSuccess"));
                    await this.fetchDeletedForms();
                    this.formToRestore = null;
                    this.selectedForm = null;
                } catch (error) {
                    console.error("Form geri yüklenemedi:", error);
                    this.$message.error(this.$t("form.trashBinList.restoreError"));
                }
            }
            this.restoreModal = false;
        },
        async confirmPermanentDelete() {
            if (this.formToDelete) {
                try {
                    await this.permanentDeleteForm({ siteId: this.site._id, formId: this.formToDelete._id });
                    this.$message.success(this.$t("form.trashBinList.deleteSuccess"));
                    await this.fetchDeletedForms();
                    this.formToDelete = null;
                    this.selectedForm = null;
                } catch (error) {
                    console.error("Form silinemedi:", error);
                    this.$message.error(this.$t("form.trashBinList.deleteError"));
                }
            }
            this.permanentDeleteModal = false;
        },
    },
    async created() {
        await this.fetchDeletedForms();
    },
};
</script>

<style lang="scss" scoped>
.form-dashboard {
    display: flex;
    gap: clamp(1.5rem, 3vw, 2rem);
    padding: clamp(1rem, 2vw, 1.5rem);
    min-height: 100%;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

#TrashBinList {
    flex: 1;
    min-width: 0;

    .header {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding: 1.5rem;
        background: white;
        border-radius: 12px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        margin-bottom: 1.5rem;
        border-left: 4px solid #ef4444;

        .header-logo {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 48px;
            height: 48px;
            background: #fef2f2;
            border-radius: 10px;

            .md-icon {
                font-size: 24px;
                color: #ef4444;
            }
        }

        .header-text {
            flex: 1;

            h1 {
                font-size: 1.25rem;
                font-weight: 600;
                color: #1f2937;
                margin: 0 0 0.25rem 0;
            }

            p {
                font-size: 0.875rem;
                color: #6b7280;
                margin: 0;
            }
        }
    }

    .container {
        background: white;
        border-radius: 12px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        padding: 1.5rem;

        .loading-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 4rem 2rem;

            .loading-spinner {
                width: 40px;
                height: 40px;
                border: 3px solid #fee2e2;
                border-top-color: #ef4444;
                border-radius: 50%;
                animation: spin 0.8s linear infinite;
                margin-bottom: 1rem;
            }

            .loading-text {
                font-size: 0.875rem;
                color: #6b7280;
                margin: 0;
            }
        }

        .form-grid {
            display: grid;
            gap: 1rem;
            max-height: 70vh;
            overflow-y: auto;
            padding-right: 0.5rem;

            &::-webkit-scrollbar {
                width: 6px;
            }

            &::-webkit-scrollbar-track {
                background: #f5f5f5;
                border-radius: 3px;
            }

            &::-webkit-scrollbar-thumb {
                background: #d1d5db;
                border-radius: 3px;

                &:hover {
                    background: #9ca3af;
                }
            }
        }

        .list-card {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 0.875rem 1rem;
            background: white;
            border: 1px solid #f1f5f9;
            border-left: 3px solid var(--border-color);
            border-radius: 6px;
            transition: all 0.2s ease;
            cursor: pointer;
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);

            &:hover {
                background: #fafafa;
                border-color: #e2e8f0;
                border-left-color: var(--border-color);
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
                transform: translateX(2px);
            }

            &.selected {
                background: #fefce8;
                border-color: #fef08a;
                border-left-color: var(--border-color);
                box-shadow: 0 2px 8px rgba(234, 179, 8, 0.15);
            }

            .card-header {
                display: flex;
                align-items: center;
                flex: 1;
                min-width: 0;
                margin-right: 1.25rem;

                .icon-section {
                    width: 32px;
                    height: 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: linear-gradient(135deg, var(--border-color), var(--border-color));
                    border-radius: 8px;
                    margin-right: 0.75rem;
                    flex-shrink: 0;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

                    i {
                        color: white;
                        font-size: 16px;
                    }
                }

                .text-section {
                    flex: 1;
                    min-width: 0;
                    display: flex;
                    flex-direction: column;
                    gap: 0.125rem;

                    .title {
                        font-size: 0.8125rem;
                        font-weight: 600;
                        color: #0f172a;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                        letter-spacing: -0.01em;
                    }

                    .deleted-date {
                        font-size: 0.6875rem;
                        color: #94a3b8;
                        font-weight: 500;
                    }

                    .actor-info {
                        display: flex;
                        flex-wrap: wrap;
                        gap: 6px;
                        margin-top: 4px;

                        .actor-item {
                            display: inline-flex;
                            align-items: center;
                            gap: 3px;
                            font-size: 0.6875rem;
                            color: rgba(107, 114, 128, 0.75);

                            i {
                                font-size: 0.75rem;
                            }

                            &.actor-trashed {
                                color: rgba(239, 68, 68, 0.7);
                            }
                        }
                    }
                }
            }

            .action-section {
                display: flex;
                gap: 0.5rem;
                flex-shrink: 0;

                .action-btn {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.375rem;
                    padding: 0.5rem 0.75rem;
                    border: none;
                    border-radius: 6px;
                    font-size: 0.6875rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    white-space: nowrap;
                    line-height: 1;
                    letter-spacing: 0.01em;

                    i {
                        font-size: 14px;
                        line-height: 1;
                    }

                    &.restore {
                        background: #ecfdf5;
                        color: #059669;
                        box-shadow: 0 1px 2px rgba(5, 150, 105, 0.1);

                        &:hover {
                            background: #10b981;
                            color: white;
                            box-shadow: 0 2px 4px rgba(5, 150, 105, 0.2);
                            transform: translateY(-1px);
                        }

                        &:active {
                            transform: translateY(0);
                        }
                    }

                    &.permanent-delete {
                        background: #fef2f2;
                        color: #dc2626;
                        box-shadow: 0 1px 2px rgba(220, 38, 38, 0.1);

                        &:hover {
                            background: #ef4444;
                            color: white;
                            box-shadow: 0 2px 4px rgba(220, 38, 38, 0.2);
                            transform: translateY(-1px);
                        }

                        &:active {
                            transform: translateY(0);
                        }
                    }
                }
            }
        }

        .empty-state {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 4rem 2rem;
            text-align: center;

            .empty-icon {
                width: 64px;
                height: 64px;
                background: #fee2e2;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                margin-bottom: 1.25rem;

                svg {
                    width: 32px;
                    height: 32px;
                    color: #ef4444;
                }
            }

            h3 {
                font-size: 1.125rem;
                font-weight: 600;
                color: #1f2937;
                margin: 0 0 0.5rem 0;
            }

            p {
                font-size: 0.875rem;
                color: #6b7280;
                margin: 0;
            }
        }
    }
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.form-details {
    width: 280px;
    flex-shrink: 0;

    .details-card {
        background: white;
        border-radius: 8px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        padding: 1.25rem;
        border: 1px solid #e5e7eb;

        .card-title {
            font-size: 1rem;
            font-weight: 600;
            color: #111827;
            margin: 0 0 0.5rem 0;
            line-height: 1.4;
            word-break: break-word;
        }

        .deleted-time {
            display: block;
            font-size: 0.75rem;
            color: #6b7280;
            margin-bottom: 1rem;
            padding-bottom: 1rem;
            border-bottom: 1px solid #f3f4f6;
        }

        .info-list {
            list-style: none;
            padding: 0;
            margin: 0 0 1.25rem 0;

            li {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 0.625rem 0;
                border-bottom: 1px solid #f3f4f6;

                &:last-child {
                    border-bottom: none;
                }

                .label {
                    font-size: 0.8125rem;
                    color: #6b7280;
                    font-weight: 500;
                }

                .value {
                    font-size: 0.9375rem;
                    font-weight: 600;
                    color: #111827;
                }
            }
        }

        .action-buttons {
            display: flex;
            gap: 0.4375rem;

            button {
                flex: 1;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 0.35rem;
                padding: 0.48rem 0.6125rem;
                border: none;
                border-radius: 5px;
                font-size: 0.6125rem;
                font-weight: 600;
                cursor: pointer;
                transition: all 0.2s ease;
                line-height: 1;

                i {
                    font-size: 11px;
                    line-height: 1;
                }

                &:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
                }

                &:active {
                    transform: translateY(0);
                }
            }

            .btn-restore {
                background: #10b981;
                color: white;

                &:hover {
                    background: #059669;
                }
            }

            .btn-delete {
                background: #ef4444;
                color: white;

                &:hover {
                    background: #dc2626;
                }
            }
        }
    }
}

@media screen and (max-width: 1024px) {
    .form-dashboard {
        flex-direction: column;
    }

    .form-details {
        width: 100%;
    }

    #TrashBinList .container .list-card {
        flex-wrap: wrap;

        .card-header {
            margin-right: 0;
            margin-bottom: 0.75rem;
            width: 100%;
        }

        .action-section {
            width: 100%;

            .action-btn {
                flex: 1;
            }
        }
    }
}
</style>

